import {
	createContext,
	useContext,
	useMemo,
	useState,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
} from 'react';

const authPrompts = ['login', 'signup'] as const;

export type AuthPrompt = (typeof authPrompts)[number];

export const isAuthPrompt = (value: string): value is AuthPrompt =>
	authPrompts.some(authPrompt => value === authPrompt);

export type AuthPromptContextValue = {
	authPrompt: AuthPrompt;
	setAuthPrompt: Dispatch<SetStateAction<AuthPrompt>>;
};

export const AuthPromptContext = createContext<AuthPromptContextValue | undefined>(undefined);

export const useAuthPrompt = () => {
	const context = useContext(AuthPromptContext);
	if (!context) {
		throw new Error('useAuthPrompt must be used within an AuthPromptProvider');
	}
	return context;
};

export type AuthPromptProviderProps = {
	defaultAuthPrompt?: AuthPrompt;
};

export default function AuthPromptProvider({
	children,
	defaultAuthPrompt = 'signup',
}: PropsWithChildren<AuthPromptProviderProps>) {
	const [authPrompt, setAuthPrompt] = useState<AuthPrompt>(defaultAuthPrompt);

	const [previousDefaultAuthPrompt, setPreviousDefaultAuthPrompt] = useState(defaultAuthPrompt);
	if (defaultAuthPrompt !== previousDefaultAuthPrompt) {
		setPreviousDefaultAuthPrompt(defaultAuthPrompt);
		setAuthPrompt(defaultAuthPrompt);
	}

	const value = useMemo<AuthPromptContextValue>(
		() => ({
			authPrompt,
			setAuthPrompt,
		}),
		[authPrompt],
	);

	return <AuthPromptContext.Provider value={value}>{children}</AuthPromptContext.Provider>;
}
